var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { BroadcastToPeerMessage, CreateSubModelMessage, DeleteModelMessage, ModelChildChangedMessage, ModelChildChangedType, ModelParentChangedMessage, ModelRoomChangedMessage, RemoveFacetFromModelMessage, RemoveModelMessage, RemoveSubModelMessage, RenderVisibilityMessage, RoomChangedMessage, SendToServerMessage, UpdateFacetOnModelMessage, LayoutCreationFinishedMessage, LayoutCompleteMessage, WorkerLocalMessage, AskOkMessage, } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { StartSessionMessage } from "@messages/session/SessionMessages";
import { UpdateLocalTransformationMessage, UpdateWorldTransformationMessage } from "@messages/scenegraph/ScenegraphMessages";
import { AttachToModelMessage } from "@messages/attachment/AttachmentMessage";
import { NewModule, MessageHandler, MessageOrMessageRequestHandler } from "@modules/module";
import { LOCAL_MESSAGING, NO_MESSAGING, resolveMetaData } from "./types";
import { ModelPath } from "./modelPath/ModelPath";
import { MODEL_POOL_ACCESS_FOR_MODEL_MODULE, modelPool } from "./ModelPool";
import { ModelMessageResolver } from "./modelMessageResolver/ModelMessageResolver";
import { computeLocalMatrixFromWorldMatrix } from "./modelUtils";
import { getModelFromAbsolutePath } from "./modelPath/modelPathUtils";
import { determineModelNameForPrinting } from "./modelPoolUtils";
import { getWorkerId } from "@messages/bus/busUtils";
/// Temporary variables to reduce GC ///
const TEMP_MAT_1 = new Matrix4();
const TEMP_MAT_2 = new Matrix4();
const TEMP_MODEL_PATH = new ModelPath("");
/**
 * This module collects all model related messages and then uses the `ModelPool` to generate models. It also forwards
 * all messages for creating, destroying and modifying models as well as modifying facets from ModelPool and Model and
 * broadcasts them either in set intervals or directly when `flushMessages` is called in ModelModule, ModelPool or
 * Model.
 */
export class ModelModule extends NewModule {
    constructor() {
        super("ModelModule");
        // Finalize model manager
        MODEL_POOL_ACCESS_FOR_MODEL_MODULE.setModelModule(this);
        this.messages = [];
        this.modelMessageResolver = new ModelMessageResolver();
    }
    /// Life cycle ///
    /**
     * Flush messages in fixed intervals
     */
    onPreRender() {
        if (this.messages.length > 0)
            this.flushMessages();
    }
    /// Messaging methods ///
    /**
     * This is called by ModelPool and Model each time an operation creates a message and messaging is not set to
     * NO_MESSAGING. This guarantees that the order of the messages is preserved
     */
    queueMessage(message, props) {
        var _a, _b, _c, _d, _e, _f;
        const messages = (_a = props === null || props === void 0 ? void 0 : props.externalMessages) !== null && _a !== void 0 ? _a : [];
        if ((_b = props === null || props === void 0 ? void 0 : props.broadcast) !== null && _b !== void 0 ? _b : true)
            messages.push(message);
        if ((_c = props === null || props === void 0 ? void 0 : props.local) !== null && _c !== void 0 ? _c : false)
            messages.push(new WorkerLocalMessage([message]));
        if ((_d = props === null || props === void 0 ? void 0 : props.server) !== null && _d !== void 0 ? _d : false)
            messages.push(new SendToServerMessage([message]));
        if ((_e = props === null || props === void 0 ? void 0 : props.peer) !== null && _e !== void 0 ? _e : false)
            messages.push(new BroadcastToPeerMessage([message]));
        // Handle message locally
        if ((props === null || props === void 0 ? void 0 : props.handleMessageInModelModule) === true)
            this.handleMessageMyself(message);
        // Messages are sent by an external source, thus we don't manage them here
        if ((props === null || props === void 0 ? void 0 : props.externalMessages) !== undefined)
            return;
        if ((_f = props === null || props === void 0 ? void 0 : props.sendImmediately) !== null && _f !== void 0 ? _f : false)
            this.connection.sendMultiple(messages);
        else if (messages.length > 0)
            this.messages.push(...messages);
    }
    /**
     * Manually sends all currently queued messages
     */
    flushMessages(props) {
        if ((props === null || props === void 0 ? void 0 : props.logMessages) === true)
            console.warn(`ModelModule.flushMessages`, [...this.messages]);
        this.connection.sendMultiple(this.messages);
        this.messages = [];
    }
    /// Model message handlers ///
    handleCreateSubModelMessage(msg, metaData) {
        var _a;
        const path = TEMP_MODEL_PATH.set(msg.path);
        if (modelPool.hasModel(msg.uid))
            return;
        // ToDo: Just for debugging purposes
        const p = path;
        const other = getModelFromAbsolutePath(p.toString());
        if (other !== undefined && other.getUID() !== msg.uid) {
            throw new Error(`Model with path "${p.toString()}" (${msg.uid}) already exists in pool but with a different uid: ${other === null || other === void 0 ? void 0 : other.getUID()}`);
        }
        if (msg.path === "") {
            console.error(`Model with uid ${msg.uid} has no associated path!`);
            return;
        }
        // We set the first room that is a child of root as the current room, only if no initial room has been set so far
        // this is necessary to guarantee that modelPool.getCurrentRoom() is (almost) always defined in other modules.
        // We need to do this only once per worker thread since later on we will be notified when a room was changed
        const isStartRoom = (path.isRoom() &&
            ((_a = path.getParent()) === null || _a === void 0 ? void 0 : _a.isRoot()) === true &&
            modelPool.getPrimaryRoom() === undefined);
        this.modelMessageResolver.createModel(path.basename(), msg.uid, path.toString(), path.isRoom(), isStartRoom, metaData);
    }
    handleRemoveModelMessage(msg, metaData) {
        const deleteChildrenOnly = RemoveModelMessage.is(msg) ? msg.childrenOnly : false;
        const handleChildren = RemoveModelMessage.is(msg) || DeleteModelMessage.is(msg) ? "destroyRecursive" : "attachToRoom";
        const deletePermanently = DeleteModelMessage.is(msg);
        modelPool.destroyModel(msg.uid, Object.assign({ destroyChildrenOnly: deleteChildrenOnly, handleChildren, destroyPermanently: deletePermanently, metaData }, NO_MESSAGING));
    }
    handleModelRoomChangedMessage(msg, metaData) {
        const model = modelPool.getModel(msg.uid);
        if (model === undefined) {
            console.error(`[model] Cannot update room of model with uid "${msg.uid}", because the model doesn't exist`);
            return;
        }
        const room = modelPool.getModel(msg.roomUID);
        if (room === undefined) {
            console.error(`[model] Cannot update room of model with name "${model.getName()}", because the room ${msg.roomUID} doesn't exist`);
            return;
        }
        if (!room.isRoom()) {
            console.error(`[model] Model with name "${model.getName()}" is not a room`);
            return;
        }
        model.setRoom(room, Object.assign({ metaData }, NO_MESSAGING));
    }
    handleModelParentChangedMessage(msg, metaData) {
        this.handleMessageOrWaitForUnresolvedModel(msg.uid, (model) => {
            this.handleMessageOrWaitForUnresolvedModel(msg.parentUID, (parent) => {
                model.setParent(parent, Object.assign({ metaData }, NO_MESSAGING));
            }, `[model] Cannot update parent of model ${determineModelNameForPrinting(msg.uid)}, because the parent ${determineModelNameForPrinting(msg.parentUID)} doesn't exist`);
        }, `[model] Cannot update parent of model ${determineModelNameForPrinting(msg.uid)}, because the model doesn't exist`);
    }
    handleModelChildChangedMessage(msg, metaData) {
        const model = modelPool.getModel(msg.uid);
        if (model === undefined) {
            console.error(`[model] Cannot update child of model ${msg.uid}, because the model doesn't exist`);
            return;
        }
        const child = modelPool.getModel(msg.childUID);
        if (child === undefined) {
            console.error(`[model] Cannot update child of model ${model.getName()}, because the child ${msg.childUID} doesn't exist`);
            return;
        }
        switch (msg.changedType) {
            case ModelChildChangedType.ADDED:
                model.addChild(child, Object.assign({ metaData }, NO_MESSAGING));
                break;
            case ModelChildChangedType.REMOVED:
                // TODO: maybe we need to add the parameters for removeChild() in ModelChildChangedMessage
                model.removeChild(child, Object.assign(Object.assign({ metaData }, NO_MESSAGING), { updateParent: "attachToNextHigherRoom" }));
                break;
        }
    }
    handleAttachToModelMessage(msg, askId, metaData) {
        const success = this.attachModelToModel(msg.childUID, msg.parentUID, msg.localTransform, metaData);
        const thisWorkerId = getWorkerId();
        if (askId !== undefined && metaData.source.workerId === thisWorkerId)
            this.connection.answer(askId, new AskOkMessage(success));
    }
    /// Appearance message handlers ///
    handleRenderVisibilityMessage(msg, metaData) {
        this.handleMessageOrWaitForUnresolvedModel(msg.uid, (model) => {
            model.setVisible(msg.visible, Object.assign({ metaData }, NO_MESSAGING));
        }, `[model] Couldn't update visibility of model ${determineModelNameForPrinting(msg.uid)} because it doesn't exist`);
    }
    handleUpdateLocalTransformationMessage(msg, metaData) {
        this.handleMessageOrWaitForUnresolvedModel(msg.uid, (model) => {
            model.setTransform(msg.transform, Object.assign({ metaData }, NO_MESSAGING));
        }, `[model] Couldn't update local transformation of model ${determineModelNameForPrinting(msg.uid)} because it doesn't exist`);
    }
    handleUpdateWorldTransformationMessage(msg, metaData) {
        this.handleMessageOrWaitForUnresolvedModel(msg.uid, (model) => {
            const worldMatrix = TEMP_MAT_1.fromArray(msg.transform);
            const localMatrix = computeLocalMatrixFromWorldMatrix(model, worldMatrix, TEMP_MAT_2);
            model.setTransform(localMatrix, Object.assign({ metaData }, NO_MESSAGING));
        }, `[model] Couldn't update world transformation of model ${determineModelNameForPrinting(msg.uid)} because it doesn't exist`);
    }
    /// Facet message handlers ///
    handleUpdateFacetOnModelMessage(msg, metaData) {
        this.handleMessageOrWaitForUnresolvedModel(msg.uid, (model) => {
            model.setFacet(msg.facet, Object.assign({ metaData }, NO_MESSAGING));
        }, `[model] Couldn't update facet "${msg.facet.pattern}" of model ${determineModelNameForPrinting(msg.uid)} because the model doesn't exist`);
    }
    handleRemoveFacetFromModelMessage(msg, metaData) {
        // TODO: Ugly conversion, if there is a better way to retrieve the facet type feel free to update this mess
        const facetType = { facetType: msg.facetType };
        this.handleMessageOrWaitForUnresolvedModel(msg.uid, (model) => {
            model.removeFacet(facetType, Object.assign({ metaData }, NO_MESSAGING));
        }, `[model] Couldn't remove facet of model ${msg.uid} because the model doesn't exist`);
    }
    /// Room message handler ///
    handleRoomChangedMessage(msg) {
        const roomModel = modelPool.getModel(msg.uid);
        if (roomModel === undefined || !roomModel.isRoom()) {
            console.warn(`[model] No room with ${msg.uid} is registered`);
            return;
        }
        // ToDo: Should the room be changed here or on LayoutCreationFinished???
        MODEL_POOL_ACCESS_FOR_MODEL_MODULE.setCurrentRoom(roomModel);
    }
    /// Session message handlers ///
    handleLayoutCreationFinishedMessage(msg) {
        if (!this.modelMessageResolver.hasUnresolvedModels()) {
            this.queueMessage(new LayoutCompleteMessage(msg.layouterIdentifier, msg.uid, msg.isFirstCreation));
            return;
        }
        // We wait until all models are resolved before we send the LayoutCompleteMessage
        this.modelMessageResolver.onAllModelsResolvedResolver.subscribe(() => {
            this.queueMessage(new LayoutCompleteMessage(msg.layouterIdentifier, msg.uid, msg.isFirstCreation));
        });
    }
    /// Model Hash Module compatibility handlers ///
    // ToDo: Remove this if its not breaking any code
    handleStartSessionMessage(_msg) {
        // try {
        //   if (msg.initialRoomId === "") throw new Error(`Start session model has no specified path!`)
        //
        //   const rootRoomModel = createModelFromPath(msg.initialRoomId, undefined, NO_MESSAGING)
        //   rootRoomModel.setTransform(new Matrix4())
        //
        //   ModelId.register(msg.initialRoomId, rootRoomModel.getUID())
        // } catch (e) {
        //   console.error(e)
        // }
    }
    /// Controller message handler ///
    // ToDo: Do we need this here?
    // @MessageHandler(MouseConnectedMessage, ControllerConnectedMessage)
    // protected handleControllerConnectedMessage(msg: MouseConnectedMessage | ControllerConnectedMessage): void {
    //   const path = new ModelPath("root/controller")
    //   this.modelMessageResolver.createModel(path.basename(), msg.uid, path.toString(), path.isRoom())
    // }
    /// Attachment helper ///
    attachModelToModel(childUID, parentUID, localTransform, metaData) {
        const model = modelPool.getModel(childUID);
        if (model === undefined) {
            const childName = determineModelNameForPrinting(childUID);
            const parentName = determineModelNameForPrinting(parentUID);
            console.error(`[model] Cannot attach model ${childName} to ${parentName}, because the model doesn't exist`);
            return false;
        }
        const parent = modelPool.getModel(parentUID);
        if (parent === undefined) {
            const childName = determineModelNameForPrinting(childUID);
            const parentName = determineModelNameForPrinting(parentUID);
            console.error(`[model] Cannot attach model ${childName} to ${parentName}, because the parent doesn't exist`);
            return false;
        }
        model.setParent(parent, Object.assign({ metaData }, LOCAL_MESSAGING));
        if (localTransform !== undefined)
            model.setTransform(localTransform, Object.assign({ metaData }, LOCAL_MESSAGING));
        return true;
    }
    /// Handle message locally helper ///
    handleMessageMyself(message) {
        const metaData = resolveMetaData();
        this.connection.handleMessageYourself(message, metaData);
    }
    /// Immediate or delayed model creation handler ///
    handleMessageOrWaitForUnresolvedModel(uid, handler, errorMessage) {
        const model = modelPool.getModel(uid);
        // Model exists, so we can resolve the handler
        if (model !== undefined) {
            handler(model);
            return;
        }
        // Is there a model that is not resolved yet? If not then we throw an error
        if (!this.modelMessageResolver.hasUnresolvedModel(uid)) {
            console.warn(errorMessage);
            return;
        }
        // Otherwise wait until the model is resolved
        this.modelMessageResolver.onModelResolvedResolver.subscribe(uid, handler);
    }
}
__decorate([
    MessageHandler(CreateSubModelMessage)
], ModelModule.prototype, "handleCreateSubModelMessage", null);
__decorate([
    MessageHandler(RemoveModelMessage, RemoveSubModelMessage, DeleteModelMessage)
], ModelModule.prototype, "handleRemoveModelMessage", null);
__decorate([
    MessageHandler(ModelRoomChangedMessage)
], ModelModule.prototype, "handleModelRoomChangedMessage", null);
__decorate([
    MessageHandler(ModelParentChangedMessage)
], ModelModule.prototype, "handleModelParentChangedMessage", null);
__decorate([
    MessageHandler(ModelChildChangedMessage)
], ModelModule.prototype, "handleModelChildChangedMessage", null);
__decorate([
    MessageOrMessageRequestHandler(AttachToModelMessage)
], ModelModule.prototype, "handleAttachToModelMessage", null);
__decorate([
    MessageHandler(RenderVisibilityMessage)
], ModelModule.prototype, "handleRenderVisibilityMessage", null);
__decorate([
    MessageHandler(UpdateLocalTransformationMessage)
], ModelModule.prototype, "handleUpdateLocalTransformationMessage", null);
__decorate([
    MessageHandler(UpdateWorldTransformationMessage)
], ModelModule.prototype, "handleUpdateWorldTransformationMessage", null);
__decorate([
    MessageHandler(UpdateFacetOnModelMessage)
], ModelModule.prototype, "handleUpdateFacetOnModelMessage", null);
__decorate([
    MessageHandler(RemoveFacetFromModelMessage)
], ModelModule.prototype, "handleRemoveFacetFromModelMessage", null);
__decorate([
    MessageHandler(RoomChangedMessage)
], ModelModule.prototype, "handleRoomChangedMessage", null);
__decorate([
    MessageHandler(LayoutCreationFinishedMessage)
], ModelModule.prototype, "handleLayoutCreationFinishedMessage", null);
__decorate([
    MessageHandler(StartSessionMessage)
], ModelModule.prototype, "handleStartSessionMessage", null);
